import React, { useRef, useState } from 'react';
import { Download, BarChart3 } from 'lucide-react';
import { useThemeStore } from '../../stores/themeStore';

interface ChartData {
  label: string;
  value: number;
  color: string;
}

interface OverviewChartProps {
  data: ChartData[];
  title: string;
}

export default function OverviewChart({ data, title }: OverviewChartProps) {
  const { darkMode } = useThemeStore();
  const chartRef = useRef<HTMLDivElement>(null);
  const [hoveredLabel, setHoveredLabel] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const maxValue = Math.max(...data.map(d => d.value), 1);
  const chartHeight = 400;
  const barWidth = 60;
  const gap = 40;
  const chartWidth = data.length * (barWidth + gap) + gap;
  const padding = { top: 40, right: 40, bottom: 80, left: 60 };

  const exportChart = () => {
    if (!chartRef.current) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = chartWidth + padding.left + padding.right;
    canvas.height = chartHeight + padding.top + padding.bottom;

    ctx.fillStyle = darkMode ? '#1f2937' : '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = darkMode ? '#ffffff' : '#000000';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(title, canvas.width / 2, 30);

    ctx.strokeStyle = darkMode ? '#374151' : '#e5e7eb';
    ctx.lineWidth = 1;

    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (chartHeight * i) / 5;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(canvas.width - padding.right, y);
      ctx.stroke();

      const value = Math.round(maxValue * (1 - i / 5));
      ctx.fillStyle = darkMode ? '#9ca3af' : '#6b7280';
      ctx.font = '12px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(value.toString(), padding.left - 10, y + 4);
    }

    data.forEach((item, index) => {
      const x = padding.left + index * (barWidth + gap) + gap;
      const barHeight = (item.value / maxValue) * chartHeight;
      const y = padding.top + chartHeight - barHeight;

      ctx.fillStyle = item.color;
      ctx.fillRect(x, y, barWidth, barHeight);

      ctx.fillStyle = darkMode ? '#ffffff' : '#000000';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(item.value.toString(), x + barWidth / 2, y - 10);

      ctx.fillStyle = darkMode ? '#9ca3af' : '#6b7280';
      ctx.font = '12px Arial';
      ctx.save();
      ctx.translate(x + barWidth / 2, canvas.height - padding.bottom + 20);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = 'right';
      ctx.fillText(item.label, 0, 0);
      ctx.restore();
    });

    const link = document.createElement('a');
    link.download = `${title.replace(/\s+/g, '_')}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
          <BarChart3 className="w-5 h-5 mr-2 text-anthea-blue" />
          {title}
        </h3>
        <button
          onClick={exportChart}
          className={`flex items-center justify-center px-4 py-2 ${darkMode ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white'} rounded-xl hover:${darkMode ? 'bg-blue-600' : 'bg-blue-700'} transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105`}
        >
          <Download className="w-4 h-4 mr-2" />
          Exporter le graphique
        </button>
      </div>

      <div ref={chartRef} className="overflow-x-auto pb-4 relative">
        <svg
          width={chartWidth + padding.left + padding.right}
          height={chartHeight + padding.top + padding.bottom}
          className="mx-auto"
        >
          <defs>
            {data.map((item, index) => (
              <linearGradient key={index} id={`gradient-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style={{ stopColor: item.color, stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: item.color, stopOpacity: 0.7 }} />
              </linearGradient>
            ))}
          </defs>

          {[0, 1, 2, 3, 4, 5].map((i) => {
            const y = padding.top + (chartHeight * i) / 5;
            const value = Math.round(maxValue * (1 - i / 5));
            return (
              <g key={i}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={chartWidth + padding.left}
                  y2={y}
                  stroke={darkMode ? '#374151' : '#e5e7eb'}
                  strokeWidth="2"
                  strokeDasharray={i === 0 ? "0" : "5,5"}
                />
                <text
                  x={padding.left - 15}
                  y={y + 5}
                  textAnchor="end"
                  className={`text-sm font-medium ${darkMode ? 'fill-gray-400' : 'fill-gray-600'}`}
                >
                  {value}
                </text>
              </g>
            );
          })}

          {data.map((item, index) => {
            const x = padding.left + index * (barWidth + gap) + gap;
            const barHeight = (item.value / maxValue) * chartHeight;
            const y = padding.top + chartHeight - barHeight;

            return (
              <g key={index} className="cursor-pointer">
                <rect
                  x={x}
                  y={padding.top + chartHeight}
                  width={barWidth}
                  height={0}
                  fill={`url(#gradient-${index})`}
                  rx="8"
                  className="transition-all duration-700 ease-out"
                  style={{
                    animation: `growBar 0.7s ease-out ${index * 0.1}s forwards`
                  }}
                >
                  <animate
                    attributeName="height"
                    from="0"
                    to={barHeight}
                    dur="0.7s"
                    begin={`${index * 0.1}s`}
                    fill="freeze"
                  />
                  <animate
                    attributeName="y"
                    from={padding.top + chartHeight}
                    to={y}
                    dur="0.7s"
                    begin={`${index * 0.1}s`}
                    fill="freeze"
                  />
                </rect>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill="transparent"
                  className="hover:fill-white hover:fill-opacity-10 transition-all duration-200"
                  rx="8"
                />
                <text
                  x={x + barWidth / 2}
                  y={y - 12}
                  textAnchor="middle"
                  className={`text-base font-bold ${darkMode ? 'fill-white' : 'fill-gray-900'}`}
                  style={{ opacity: 0 }}
                >
                  {item.value}
                  <animate
                    attributeName="opacity"
                    from="0"
                    to="1"
                    dur="0.5s"
                    begin={`${index * 0.1 + 0.5}s`}
                    fill="freeze"
                  />
                </text>
                <text
                  x={x + barWidth / 2}
                  y={chartHeight + padding.top + 20}
                  textAnchor="end"
                  transform={`rotate(-45, ${x + barWidth / 2}, ${chartHeight + padding.top + 20})`}
                  className={`text-sm font-medium ${darkMode ? 'fill-gray-300' : 'fill-gray-700'} cursor-pointer`}
                  onMouseEnter={(e) => {
                    setHoveredLabel(index);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipPosition({ x: rect.left, y: rect.top - 10 });
                  }}
                  onMouseLeave={() => setHoveredLabel(null)}
                >
                  {item.label}
                </text>
              </g>
            );
          })}
        </svg>

        {hoveredLabel !== null && (
          <div
            className={`fixed z-50 px-3 py-2 rounded-lg shadow-lg ${
              darkMode ? 'bg-gray-900 text-white border border-gray-700' : 'bg-white text-gray-900 border border-gray-200'
            } text-sm font-medium pointer-events-none`}
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {data[hoveredLabel].label}
          </div>
        )}
      </div>
    </div>
  );
}
